import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { actionPillCssVars } from '../ui/actionPillTokens';
import { MENU_CLOSE_DWELL_MS } from '../../lib/uiMotion';
import {
    activeMentionQuery,
    applyMention,
    cantSeeMessage,
    filterMentionCandidates,
    loadMentionCandidates,
    pruneMentions,
} from '../../lib/noteMentions';

// Staff Messaging Phase 1 (1.12.235-beta): the internal-note box with "@" mentions.
//   Typing "@" (at the start or after a space) opens a list of the company's active staff, filtered by name as you type.
//   ↑/↓ move, Enter or Tab chooses, Escape closes; tap or click works too. The list is an ARIA listbox owned by the box (combobox).
//   The chosen person goes into the text as "@Name" and into the note's mention list (user ids). Below the box, each mentioned person
//   shows as a highlighted chip; anyone who cannot see this conversation gets the line "<Name> can't see this conversation — invite
//   them first" (with Invite when you can invite). The database decides who is alerted; this only shapes what the box shows.
// The list sits in the notes section, below the box (never over the reply composer above it).
// The list uses the shared menu-pill look (actionPillTokens) and the shared 2-second rule: after the box loses focus it stays
// open for MENU_CLOSE_DWELL_MS unless the pointer is on it, so a slow tap still lands.

export default function MentionNoteInput({
    client,
    sessionId,
    value,
    onChange,
    mentions = [],
    onMentionsChange,
    // the mentions of the note just posted: their "can't see this conversation" lines stay until the next note is started
    savedMentions = null,
    onSubmit,
    inputRef,
    dark = false,
    onInvite = null,
    placeholder = 'Add an internal note… Type @ to mention a colleague',
    className = '',
}) {
    const ids = useId();
    const listId = `${ids}-mentions`;
    const localRef = useRef(null);
    const boxRef = inputRef || localRef;
    const [candidates, setCandidates] = useState([]);
    const [loaded, setLoaded] = useState('');
    const [range, setRange] = useState(null);
    const [active, setActive] = useState(0);
    const closeTimerRef = useRef(null);
    const pointerInListRef = useRef(false);

    // The people list, once per conversation (only when "@" is first typed).
    const ensureCandidates = useCallback(() => {
        if (!sessionId || loaded === sessionId) return;
        setLoaded(sessionId);
        loadMentionCandidates(client, sessionId).then((rows) => setCandidates(rows));
    }, [client, loaded, sessionId]);
    useEffect(() => { setCandidates([]); setLoaded(''); setRange(null); }, [sessionId]);
    useEffect(() => () => clearTimeout(closeTimerRef.current), []);

    const options = useMemo(() => (range ? filterMentionCandidates(candidates, range.query) : []), [candidates, range]);
    const open = Boolean(range) && options.length > 0;
    // PR #779 review: the list opens BELOW the note box, inside the notes section, so it never covers the reply composer or the
    // thread controls above it. Bring it into view when it opens near the bottom edge.
    const listRef = useRef(null);
    useEffect(() => {
        if (!open) return;
        const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        listRef.current?.scrollIntoView?.({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
    }, [open]);
    const activeIndex = Math.min(active, Math.max(0, options.length - 1));

    const updateRange = (text, caret) => {
        const next = activeMentionQuery(text, caret);
        setRange(next);
        if (next) {
            ensureCandidates();
            setActive(0);
        }
    };

    const choose = (person) => {
        if (!person || !range) return;
        const result = applyMention(value, range, person);
        onChange?.(result.text);
        onMentionsChange?.(pruneMentions(result.text, [...mentions, result.mention]));
        setRange(null);
        requestAnimationFrame(() => {
            const box = boxRef.current;
            if (!box) return;
            box.focus();
            try { box.setSelectionRange(result.caret, result.caret); } catch { /* not focusable yet */ }
        });
    };

    const handleChange = (event) => {
        const text = event.target.value;
        onChange?.(text);
        onMentionsChange?.(pruneMentions(text, mentions));
        updateRange(text, event.target.selectionStart);
    };

    const handleKeyDown = (event) => {
        if (open) {
            if (event.key === 'ArrowDown') { event.preventDefault(); setActive((activeIndex + 1) % options.length); return; }
            if (event.key === 'ArrowUp') { event.preventDefault(); setActive((activeIndex - 1 + options.length) % options.length); return; }
            if (event.key === 'Enter' || event.key === 'Tab') { event.preventDefault(); choose(options[activeIndex]); return; }
            if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setRange(null); return; }
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            onSubmit?.();
        }
    };

    const handleBlur = () => {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = setTimeout(() => {
            if (!pointerInListRef.current && document.activeElement !== boxRef.current) setRange(null);
        }, MENU_CLOSE_DWELL_MS);
    };

    const byId = useMemo(() => new Map(candidates.map((row) => [row.user_id, row])), [candidates]);
    const blocked = (mentions.length ? mentions : (savedMentions || [])).filter((mention) => byId.get(mention.user_id)?.can_view === false);
    const chipCls = 'ia-mention-chip';

    return (
        <div className={`relative min-w-0 flex-1 ${className}`} data-mention-input="true">
            <textarea
                ref={boxRef}
                rows={1}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onKeyUp={(event) => { if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) updateRange(event.currentTarget.value, event.currentTarget.selectionStart); }}
                onClick={(event) => updateRange(event.currentTarget.value, event.currentTarget.selectionStart)}
                onFocus={() => clearTimeout(closeTimerRef.current)}
                onBlur={handleBlur}
                placeholder={placeholder}
                className={`min-h-8 w-full resize-y rounded border px-2 py-1 text-sm ${dark ? 'border-gray-600 bg-gray-900 text-gray-100' : 'border-gray-300 bg-white'}`}
                aria-label="Add internal note"
                role="combobox"
                aria-autocomplete="list"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? listId : undefined}
                aria-activedescendant={open ? `${listId}-${options[activeIndex]?.user_id}` : undefined}
            />
            {open && (
                <ul
                    ref={listRef}
                    id={listId}
                    role="listbox"
                    aria-label="Mention a colleague"
                    className="ia-mention-picker"
                    style={actionPillCssVars()}
                    data-mention-picker="true"
                    onPointerEnter={() => { pointerInListRef.current = true; clearTimeout(closeTimerRef.current); }}
                    onPointerLeave={() => { pointerInListRef.current = false; handleBlur(); }}
                >
                    {options.map((person, index) => (
                        <li
                            key={person.user_id}
                            id={`${listId}-${person.user_id}`}
                            role="option"
                            aria-selected={index === activeIndex}
                            data-active={index === activeIndex ? 'true' : undefined}
                            className="ia-mention-option"
                            data-mention-option={person.user_id}
                            // mousedown/touch: choose before the box loses focus
                            onPointerDown={(event) => { event.preventDefault(); choose(person); }}
                            onMouseEnter={() => setActive(index)}
                        >
                            <span className="ia-mention-option__name">{person.name}</span>
                            {!person.can_view && <span className="ia-mention-option__hint">Can&apos;t see this</span>}
                        </li>
                    ))}
                </ul>
            )}
            {mentions.length > 0 && (
                <div className="mt-1 flex flex-wrap items-center gap-1" data-mention-chips="true" aria-label="Mentioned in this note">
                    {mentions.map((mention) => (
                        <span key={mention.user_id} className={chipCls} data-mention-chip={mention.user_id}>@{mention.label}</span>
                    ))}
                </div>
            )}
            {blocked.map((mention) => (
                <p key={mention.user_id} role="status" className="ia-mention-blocked mt-1 text-xs" data-mention-blocked={mention.user_id}>
                    {cantSeeMessage(byId.get(mention.user_id)?.name || mention.label)}
                    {typeof onInvite === 'function' && (
                        <>
                            {' '}
                            <button type="button" className="ia-mention-invite" onClick={() => onInvite(mention.user_id)} data-mention-invite={mention.user_id}>
                                Invite {byId.get(mention.user_id)?.name || mention.label}
                            </button>
                        </>
                    )}
                </p>
            ))}
        </div>
    );
}
